import * as carrierFormInputActions from '~/actions/carrier-form-input.actions';
import { ActionKeys } from '~/actions/constants';
import { sendTrackEvent } from '~/actions/prod-analytics.actions';
import { EBAY_CANADA_REGISTRATION_COOKIE_NAME } from '~/constants/cookies';
import * as Cookies from 'js-cookie';
import {
  CarrierFormEventType,
  CarrierFormType,
  TabbedCarrierFormName,
} from '~/pages/Settings/Carriers/CarrierForm/CarrierForm';
import { call, put, takeLatest } from 'redux-saga/effects';
import { getPathname } from '~/selectors/router.selectors';
import { selectState } from '..';

const ordersPageName = 'Orders Page'; // Find a better place for these constants for any event that is page-specific to ensure Heap tag name consistency
const carriersPageName = 'Carrier Settings Page';

export function* trackCarrierFormEvent(
  action: ReturnType<typeof carrierFormInputActions.trackCarrierFormEvent>,
) {
  const formType = action.formType || '';
  const eventType = action.eventType;
  const formName = action.formName;

  const pathName: ReturnType<typeof getPathname> =
    yield selectState<ReturnType<typeof getPathname>>(getPathname);
  const isOnOrdersPage = pathName === '/orders';
  const isOnCarrierSettingsPage = pathName === '/settings/carriers';
  const trackEventName = getTrackEventName(
    formType,
    eventType,
    formName,
    isOnOrdersPage,
    isOnCarrierSettingsPage,
  );

  if (trackEventName !== '') {
    yield put(sendTrackEvent(trackEventName));
  }

  const ebayCanadaConnectionStartCookie = Cookies.get(EBAY_CANADA_REGISTRATION_COOKIE_NAME);

  if (
    ebayCanadaConnectionStartCookie === 'true' &&
    formName === TabbedCarrierFormName.EbayCanadaPost &&
    (eventType === CarrierFormEventType.Close || eventType === CarrierFormEventType.Submit)
  ) {
    if (eventType === CarrierFormEventType.Close) {
      // Removal of the cookie when the form is submitted is handled in removeEbayRegistrationCookie below
      // to ensure the cookie only gets removed if the submit was successful.
      yield call(Cookies.remove, EBAY_CANADA_REGISTRATION_COOKIE_NAME, {
        domain: CONFIG.shippoRootUrl,
      });
    }
  }
}

const getTrackEventName = (
  formType: string | CarrierFormType,
  eventType: CarrierFormEventType,
  formName: TabbedCarrierFormName,
  isOnOrdersPage: boolean,
  isOnCarrierSettingsPage: boolean,
): string => {
  let pageLocation = '';
  if (isOnOrdersPage) {
    pageLocation = ordersPageName;
  } else if (isOnCarrierSettingsPage) {
    pageLocation = carriersPageName;
  }

  if (pageLocation === '') return ''; // handle an invalid pathName

  let suffix = eventType === CarrierFormEventType.Click ? 'Tab' : 'Form'; // add more sophisticated logic if we need to track more types of clicks

  let formTypeName = '';
  if (formType && formType !== '') {
    formTypeName = formType.charAt(0).toUpperCase() + formType.slice(1); // Capitalize first letter
  }

  // ex. "Ebay Canada Post Orders Page - Click Create Tab"
  let trackEventString = `${formName} ${pageLocation} - ${eventType} ${formTypeName} ${suffix}`;

  // modal closed event will have no formType
  if (eventType === CarrierFormEventType.Close) {
    suffix = 'Modal';
    // ex. "Ebay Canada Post Orders Page - Close Modal"
    trackEventString = `${formName} ${pageLocation} - ${eventType} ${suffix}`;
  }

  return trackEventString;
};

export function* removeEbayRegistrationCookie() {
  const ebayCanadaConnectionStartCookie = Cookies.get(EBAY_CANADA_REGISTRATION_COOKIE_NAME);

  if (ebayCanadaConnectionStartCookie === 'true') {
    yield call(Cookies.remove, EBAY_CANADA_REGISTRATION_COOKIE_NAME, {
      domain: CONFIG.shippoRootUrl,
    });
  }
}

export default [
  takeLatest(ActionKeys.CARRIER_FORM_INPUT_EVENT, trackCarrierFormEvent),
  takeLatest(
    [
      ActionKeys.CREATE_CARRIER_ACCOUNT_SUCCESS,
      ActionKeys.CONNECT_CARRIER_ACCOUNT_SUCCESS,
      ActionKeys.CLEAR_EBAY_CANADA_CONNECT_START_COOKIE,
    ],
    removeEbayRegistrationCookie,
  ),
];
